import React, { useEffect, useRef, useState } from 'react';
import { log } from 'log';
import { pick, prop } from 'ramda';

import { useCombinedOnClose, useOnClose } from '@core/js/components/common/PopupManager';

import { Loader } from '@interlayer/js/components/common/Loader/Loader';
import { ManagedPopup } from '@interlayer/js/components/common/Popup/ManagedPopup';

import { getTemporaryAuthenticationToken } from '@core/js/hooks/auth/server';
import { useMountEffect } from '@core/js/hooks/useMountEffect';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';

import { useConfigTag } from '@interlayer/js/app/hooks/useConfigTag';
import { addHandler, removeHandler } from '@interlayer/js/app/services/postMessage.service';
import { CONFIG_TAG_TYPE } from '@interlayer/js/components/system/TagManager/buzz/constants';
import { TAtpSettingsTagConfig } from '@interlayer/js/components/system/TagManager/buzz/types';

import { TAtpPopupProps } from './types';

import style from './AtpPopup.scss';

export const AtpPopup: React.FC<TAtpPopupProps> = ({ id, config: { schedule, type, name } }) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const onClose = useOnClose(id);
    const closePopup = useCombinedOnClose(onClose, id);
    const scheduleType = Number(schedule) ? 'scheduleId' : 'scheduleKey';
    const [src, setSrc] = useState('');
    const { userName = '', sessionToken } = useUserSelector(pick(['userName', 'sessionToken']));
    const casinoName = useAppSelector(prop('casinoName'));
    const { config: { url, width, height } } = useConfigTag(CONFIG_TAG_TYPE.atpSettings) as TAtpSettingsTagConfig;

    useEffect(() => {
        getTemporaryAuthenticationToken({
            userName, casinoName, serviceType: 'GamePlay', sessionToken
        }).then((response) => {
            setSrc(`${url}?component=atp&gameName=${name}&gameType=${type}&${scheduleType}=${schedule}&token=${userName}:${response.data.token}`);
        });
    }, [casinoName, sessionToken, schedule, type, name, userName, url, scheduleType]);

    useMountEffect(() => {
        if (!iframeRef.current || !src) {
            return;
        }

        const handleMessage = (message: { classId?: number }, parseError: string) => {
            if (parseError) {
                log('httpReq', `Message from Atp iframe can't be parsed: ${message}`);
                return;
            }

            if (message?.classId === 101) {
                closePopup();
            }
        };

        const handlerId = addHandler({
            source: iframeRef.current?.contentWindow,
            callback: handleMessage,
            parse: true
        });

        return () => {
            removeHandler(handlerId);
        };
    }, [iframeRef, src]);

    return (
        <ManagedPopup
            isModal
            onClose={onClose}
            className={style.popup}
            classNames={{ inner: style.inner, isModal: style.isModal }}
        >
            {src
                ? (
                    <iframe
                        src={src}
                        ref={iframeRef}
                        className={style.wrapper}
                        style={{ width, height }}
                    />
                )
                : (
                    <Loader />
                )
            }
        </ManagedPopup>
    );
};
